import React, { useEffect, useRef, useState } from "react";
import LandscapeProgramLayout from "../LandscapeProgramLayout";
import BottomNav from "../BottomNav";
import cn from "../../../utils/cn";
import { Pause, Play, Triangle } from "../../Icons";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useAuthContext } from "../../../context/auth";
import EventEmitter from "eventemitter3";

import useAudio from "../../../hooks/useAudio";
import interpolate from "../../../utils/interpolate";

function Line() {
  return (
    <svg
      width="4"
      height="92"
      viewBox="0 0 4 92"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        x1="2"
        y1="-8.74228e-08"
        x2="2"
        y2="92"
        stroke="url(#paint0_linear_5264_1049)"
        stroke-width="4"
        stroke-dasharray="6 6"
      />
      <defs>
        <linearGradient
          id="paint0_linear_5264_1049"
          x1="0"
          y1="0"
          x2="-1.99976"
          y2="0.0217366"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#E3FAFF" />
          <stop offset="1" stop-color="#53CAF5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function Fit({
  number,
  count,
  isActive,
}: {
  number: number;
  count: number;
  isActive: boolean;
}) {
  const time = 120;

  return (
    <div className="gap-[50px] flex items-center">
      <h1 className="text-[48px] font-bold text-white">{number}SET</h1>
      <div
        className="w-[1000px] h-[100px] rounded-[100px] flex items-center justify-center relative"
        style={{
          border: "4px solid transparent",
          backgroundImage: "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)",
          backgroundOrigin: "border-box",
          // backgroundClip: "content-box, border-box",
        }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2">
          <Line />
        </div>
        <div className="w-full h-full bg-bg rounded-[100px] flex items-center">
          <div
            className="w-[984px] h-[84px] rounded-[84px] flex justify-end mx-2 relative"
            style={{
              backgroundImage:
                "linear-gradient(270deg, #53CAF5 0%, rgba(44, 218, 0, 0.00) 100%)",
              transition: "all 0.5s ease",
              width: `${interpolate((count / time) * 100)}%`,
              minWidth: "84px",
            }}
          >
            <div
              className="w-[84px] h-[84px] rounded-[100px]"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)",
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkoutType1({ secondRound }: { secondRound?: boolean }) {
  const {
    emitter,
    sendSetStep,
    step,
    sendStartCountDown,
    sendStopCountDown,
    did,
  } = useAuthContext();

  const SET_TIME = 120; // 각 세트당 120초
  const TOTAL_SETS = 4;
  const TOTAL_TIME = SET_TIME * TOTAL_SETS; // 480초

  const [totalCount, setTotalCount] = useState(0);
  const [stopped, setStopped] = useState(true); // Start as stopped, will be set to false when timer starts
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const syncStartTimeRef = useRef<number | null>(null);
  const pausedAtRef = useRef<number>(0); // 멈췄을 때의 경과 시간
  const playHalfway = useAudio("thing.mp3");
  const playCountdown = useAudio("countdown.mp3");

  // 현재 활성화된 SET 번호 계산
  const currentSet = Math.floor(totalCount / SET_TIME) + 1;
  // 현재 SET 내에서의 카운트
  const currentSetCount = totalCount % SET_TIME;

  useEffect(() => {
    console.log("[WorkoutType1] Mount, starting immediately");
    syncStartTimeRef.current = Date.now();
    pausedAtRef.current = 0;
    setStopped(false);

    const handleResume = () => {
      console.log("[WorkoutType1] Resume event received, pausedAt:", pausedAtRef.current);
      // Resume from where we paused
      syncStartTimeRef.current = Date.now() - pausedAtRef.current * 1000;
      setStopped(false);
    };

    const handlePause = () => {
      console.log("[WorkoutType1] Pause event received, totalCount:", totalCount);
      // Save current progress
      pausedAtRef.current = totalCount;
      setStopped(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    emitter.current.on("resume", handleResume);
    emitter.current.on("pause", handlePause);

    return () => {
      emitter.current.off("resume", handleResume);
      emitter.current.off("pause", handlePause);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log("[WorkoutType1] Timer effect, stopped:", stopped, "syncStartTime:", syncStartTimeRef.current);

    if (!stopped && syncStartTimeRef.current) {
      const startTimestamp = syncStartTimeRef.current;

      const tick = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTimestamp;

        // Calculate total seconds passed
        const secondsPassed = Math.floor(elapsed / 1000);
        const newCount = Math.min(TOTAL_TIME, secondsPassed);

        setTotalCount((prev) => {
          if (newCount !== prev) {
            console.log("[WorkoutType1] Count updated:", prev, "->", newCount);

            // Check for halfway point sound (60초) - trigger when crossing 60 second mark
            const prevInSet = prev % SET_TIME;
            const newInSet = newCount % SET_TIME;

            if (prevInSet < 60 && newInSet >= 60) {
              console.log("[WorkoutType1] Playing halfway sound");
              did === "1" && playHalfway();
            }

            // Check for countdown sound (4초 남았을 때) - trigger when remaining becomes 4 or less
            const prevRemaining = SET_TIME - prevInSet;
            const newRemaining = SET_TIME - newInSet;

            if (prevRemaining > 4 && newRemaining <= 4 && newRemaining > 0) {
              console.log("[WorkoutType1] Playing countdown sound, remaining:", newRemaining);
              did === "1" && playCountdown();
            }

            // Check if finished
            if (newCount >= TOTAL_TIME) {
              console.log("[WorkoutType1] Finished! Moving to next step");
              sendSetStep(step + 1);
              return TOTAL_TIME;
            }
          }
          return newCount;
        });

        // Calculate next tick to align with second boundaries
        const nextExpectedTime =
          startTimestamp + (Math.floor(elapsed / 1000) + 1) * 1000;
        const delay = nextExpectedTime - Date.now();

        if (newCount < TOTAL_TIME) {
          timerRef.current = setTimeout(tick, Math.max(0, delay));
        }
      };

      // Start immediately
      console.log("[WorkoutType1] Starting tick loop");
      tick();

      return () => {
        console.log("[WorkoutType1] Cleaning up timer");
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [stopped, step, sendSetStep, did, playHalfway, playCountdown, TOTAL_TIME, SET_TIME]);

  return (
    <LandscapeProgramLayout step={4}>
      <>
        <div className="bg-bg w-full relative h-full flex">
          <div className="flex flex-col gap-[50px] ml-[59px] flex-1 justify-center">
            {Array(TOTAL_SETS)
              .fill(0)
              .map((_, i) => (
                <Fit
                  number={i + 1}
                  count={i + 1 === currentSet ? currentSetCount : i + 1 < currentSet ? SET_TIME : 0}
                  isActive={i + 1 <= currentSet && !stopped}
                  key={i}
                />
              ))}
          </div>
          <div className="absolute left-[1440px] top-1/2 -translate-y-1/2 flex flex-col items-center w-[420px]">
            <h2 className="text-[80px] font-bold text-white">
              {secondRound ? 2 : 1}/2R
            </h2>
            <h2 className="text-[140px] font-bold text-white">
              {String(Math.floor((TOTAL_TIME - totalCount) / 60)).padStart(2, "0")}:
              {String((TOTAL_TIME - totalCount) % 60).padStart(2, "0")}{" "}
            </h2>
            <button
              onClick={() => {
                if (stopped) {
                  sendStartCountDown();
                } else {
                  sendStopCountDown();
                }
              }}
            >
              {stopped ? <Play /> : <Pause />}
            </button>
          </div>
        </div>
      </>
      <BottomNav />
    </LandscapeProgramLayout>
  );
}

export default WorkoutType1;
